import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_API = `${BACKEND_URL}/api/auth`;
const TOKEN_KEY = "friedrich_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common["Authorization"];
    }
    setToken(t);
  }, []);

  // Verify stored token on mount
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    axios.get(`${AUTH_API}/me`)
      .then(r => setUser(r.data))
      .catch(() => { applyToken(null); setUser(null); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const r = await axios.post(`${AUTH_API}/login`, { email, password });
    applyToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (email, password, name) => {
    const r = await axios.post(`${AUTH_API}/register`, { email, password, name });
    if (r.data.pending) return { pending: true };
    applyToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    applyToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    try {
      const r = await axios.get(`${AUTH_API}/me`);
      setUser(r.data);
    } catch {
      // ignore
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
